import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useSmoothScroll } from "@/lib/smooth-scroll";

const NAV = [
  { id: "sobre", label: "Sobre" },
  { id: "servicos", label: "Serviços" },
  { id: "comodidades", label: "Comodidades" },
  { id: "galeria", label: "Galeria" },
  { id: "contactos", label: "Contactos" },
];

export default function Header() {
  const { scrollTo } = useSmoothScroll();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollTo(`#${id}`);
  };

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-cream/80 border-b border-border/50"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12 h-20 flex items-center justify-between">
        <button
          data-testid="brand-logo"
          onClick={() => scrollTo(0)}
          className={`font-display text-2xl lg:text-[1.65rem] tracking-tight leading-none transition-colors ${
            scrolled ? "text-forest" : "text-cream"
          }`}
        >
          Casa <span className="italic">Montebello</span>
        </button>

        <nav className="hidden lg:flex items-center gap-10">
          {NAV.map((item) => (
            <button
              key={item.id}
              data-testid={`nav-${item.id}`}
              onClick={() => go(item.id)}
              className={`relative text-[0.78rem] tracking-[0.22em] uppercase font-medium transition-colors group ${
                scrolled ? "text-forest/80 hover:text-forest" : "text-cream/85 hover:text-cream"
              }`}
            >
              {item.label}
              <span className={`absolute -bottom-1 left-0 h-px w-0 group-hover:w-full transition-all duration-300 ${scrolled ? "bg-forest" : "bg-cream"}`} />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="reservar-agora-header-btn"
            onClick={() => go("reservar")}
            className={`hidden sm:inline-flex items-center px-6 h-11 rounded-full text-[0.78rem] tracking-[0.2em] uppercase font-medium transition-all ${
              scrolled ? "bg-forest text-cream hover:bg-[#1f2f21]" : "bg-cream text-forest hover:bg-white"
            }`}
          >
            Reservar
          </button>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className={`lg:hidden p-2 ${scrolled ? "text-forest" : "text-cream"}`}
            aria-label="Menu"
          >
            {open ? <X strokeWidth={1.5} /> : <Menu strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden backdrop-blur-xl bg-cream/95 border-t border-border/60"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {NAV.map((item) => (
                <button
                  key={item.id}
                  data-testid={`mobile-nav-${item.id}`}
                  onClick={() => go(item.id)}
                  className="text-left py-3 font-display text-2xl text-forest border-b border-border/60"
                >
                  {item.label}
                </button>
              ))}
              <button
                data-testid="reservar-agora-mobile-btn"
                onClick={() => go("reservar")}
                className="mt-4 inline-flex items-center justify-center h-12 rounded-full bg-forest text-cream text-[0.78rem] tracking-[0.2em] uppercase font-medium"
              >
                Reservar Agora
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
